/**
 * DashboardLayout Component
 *
 * Main dashboard layout with role-based navigation:
 * - Admin: Accordion navigation with collapsible categories
 * - Guide/Customer: Flat navigation with simple list
 * - Mobile: Bottom tab navigation (<768px)
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: US-1.21.2-07 - Dashboard Layout System
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content to render in main area
 * @param {string} props.userRole - User role: 'admin' | 'guide' | 'customer'
 * @param {string} props.activeNavId - Currently active navigation item ID
 * @param {Function} props.onNavigate - Navigation callback function
 * @param {Object} props.user - User object with name, email, avatar
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Header from './Header';
import Footer from './Footer';
import FloatingSidebar from './FloatingSidebar';
import MobileBottomTabs from './MobileBottomTabs';
import {
  LayoutDashboard, UserCheck, BarChart3, FlaskConical, Users as _Users,
  AlertCircle as _AlertCircle, Activity, MessageSquare, Leaf, Backpack, MoreHorizontal,
  Compass, Calendar, ClipboardList as _ClipboardList, Home as _Home, MessageCircle, Star as _Star, User as _User, ListTodo, DollarSign,
  XCircle as _XCircle, Scale as _Scale, CalendarDays, RotateCcw, CreditCard,
  UserCog, Inbox, Globe, MousePointerClick, Clock, Megaphone, Contact, FileText, CalendarCheck,
  Gavel, HeartPulse
} from 'lucide-react';

// SOURCE OF TRUTH: All navigation items are defined here.
// Do not create separate config files — items added there will never appear in the sidebar.
const adminNavigation = [
  {
    category: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' }
    ]
  },
  {
    category: 'Operations',
    items: [
      { id: 'users', label: 'User Management', icon: UserCog, path: '/admin/users' },
      { id: 'approvals', label: 'Guide Approvals', icon: UserCheck, path: '/admin/guide-approvals' },
      { id: 'experiences', label: 'Experiences', icon: Compass, path: '/admin/experiences' },
      { id: 'bookings', label: 'Bookings', icon: CalendarDays, path: '/admin/bookings' },
      { id: 'cancellations', label: 'Cancellations', icon: _XCircle, path: '/admin/cancellations' },
      { id: 'disputes', label: 'Disputes', icon: _Scale, path: '/admin/disputes' },
      { id: 'review-moderation', label: 'Review Moderation', icon: _ClipboardList, path: '/admin/review-moderation' },
      { id: 'refunds', label: 'Refund Management', icon: RotateCcw, path: '/admin/refunds' },
      { id: 'stuck-refunds', label: 'Stuck Refunds', icon: _AlertCircle, path: '/admin/stuck-refunds' },
      { id: 'support', label: 'Support Queue', icon: ListTodo, path: '/support/queue' },
      { id: 'referrals', label: 'Referrals', icon: _Users, path: '/admin/referrals' },
      { id: 'accounting', label: 'Accounting', icon: FileText, path: '/admin/accounting' }
    ]
  },
  {
    category: 'Analytics',
    items: [
      { id: 'analytics', label: 'Platform Analytics', icon: BarChart3, path: '/admin/analytics' },
      { id: 'ab-tests', label: 'A/B Tests', icon: FlaskConical, path: '/admin/ab-tests' }
    ]
  },
  {
    category: 'Marketing',
    items: [
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone, path: '/admin/marketing/campaigns' },
      { id: 'markets', label: 'Markets', icon: Globe, path: '/admin/markets' },
      { id: 'leads', label: 'Leads', icon: Contact, path: '/admin/leads' },
      { id: 'lead-actions', label: 'Lead Actions', icon: MousePointerClick, path: '/admin/lead-actions' },
      { id: 'events', label: 'Events', icon: CalendarCheck, path: '/admin/marketing/events' },
      { id: 'scheduling', label: 'Scheduling', icon: Clock, path: '/admin/marketing/scheduling' }
    ]
  },
  {
    category: 'Platform Data',
    items: [
      { id: 'species', label: 'Species Catalog', icon: Leaf, path: '/admin/species-catalog' },
      { id: 'gear', label: 'Gear Catalog', icon: Backpack, path: '/admin/gear-catalog' },
      { id: 'legal-documents', label: 'Legal Documents', icon: Gavel, path: '/admin/legal-documents' }
    ]
  },
  {
    category: 'System',
    items: [
      // SCRUM-2307: SystemHealthDashboard is routed at /admin/system-health
      // (apps/web/src/routes.jsx:1092) but had no SYSTEM-category nav entry,
      // violating SCRUM-2176 AC-05 (the page was unreachable from the sidebar).
      { id: 'system-health', label: 'System Health', icon: HeartPulse, path: '/admin/system-health' },
      { id: 'services', label: 'Services Usage', icon: Activity, path: '/admin/services-usage' },
      { id: 'errors', label: 'Error Monitoring', icon: _AlertCircle, path: '/admin/errors' },
      { id: 'queue', label: 'Message Queue', icon: MessageSquare, path: '/admin/message-queue' },
      { id: 'dlq', label: 'DLQ Monitor', icon: Inbox, path: '/admin/dlq' }
    ]
  }
];

const guideNavigation = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/guide/dashboard' },
  { id: 'bookings', label: 'Bookings', icon: Calendar, path: '/guide/bookings' },
  { id: 'availability', label: 'Availability', icon: CalendarDays, path: '/guide/availability' },
  { id: 'reviews', label: 'Reviews', icon: _Star, path: '/guide/reviews' },
  { id: 'experiences', label: 'Experiences', icon: Compass, path: '/guide/experiences' },
  { id: 'messages', label: 'Messages', icon: MessageSquare, path: '/guide/messages' },
  { id: 'earnings', label: 'Earnings', icon: DollarSign, path: '/guide/earnings' },
  { id: 'accounting', label: 'Accounting', icon: FileText, path: '/guide/accounting' },
  { id: 'payment-settings', label: 'Payment Settings', icon: CreditCard, path: '/guide/dashboard/settings/payments' },
  { id: 'profile', label: 'Profile', icon: _User, path: '/guide/profile' }
];

const customerNavigation = [
  { id: 'dashboard', label: 'Dashboard', icon: _Home, path: '/customer/dashboard' },
  { id: 'trips', label: 'My Trips', icon: Backpack, path: '/customer/trips' },
  { id: 'messages', label: 'Messages', icon: MessageCircle, path: '/customer/messages' },
  { id: 'reviews', label: 'Reviews', icon: _Star, path: '/customer/reviews' },
  { id: 'settings', label: 'Settings', icon: _User, path: '/account/settings' }
];

// Mobile navigation (5 items max)
const adminMobileNav = [
  { id: 'dashboard', label: 'Home', icon: LayoutDashboard, path: '/admin/dashboard' },
  { id: 'approvals', label: 'Approvals', icon: UserCheck, path: '/admin/guide-approvals' },
  { id: 'support', label: 'Support', icon: ListTodo, path: '/support/queue' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
  { id: 'more', label: 'More', icon: MoreHorizontal, isMore: true }
];

const guideMobileNav = guideNavigation.slice(0, 5);
const customerMobileNav = customerNavigation.slice(0, 5);

const DashboardLayout = ({
  children,
  userRole = 'guide',
  activeNavId = 'dashboard',
  onNavigate,
  user: _user = { name: 'User', email: 'user@example.com', avatar: null }
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Detect screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get navigation configuration based on role
  const getNavigation = () => {
    switch (userRole) {
      case 'admin':
        return { desktop: adminNavigation, mobile: adminMobileNav };
      case 'guide':
        return { desktop: guideNavigation, mobile: guideMobileNav };
      case 'customer':
        return { desktop: customerNavigation, mobile: customerMobileNav };
      default:
        return { desktop: guideNavigation, mobile: guideMobileNav };
    }
  };

  const navigation = getNavigation();

  return (
    <div style={styles.layoutContainer}>
      {/* Site-wide Header (existing component) */}
      <Header />

      {/* Dashboard Content */}
      <div style={styles.mainWrapper}>
        {/* Desktop/Tablet Sidebar - Hidden on mobile */}
        {!isMobile && (
          <FloatingSidebar
            navigation={navigation.desktop}
            userRole={userRole}
            activeNavId={activeNavId}
            onNavigate={onNavigate}
            isCollapsed={isTablet}
          />
        )}

        {/* Main Content - Centered with max-width */}
        <main style={styles.mainContent}>
          <div style={styles.contentContainer}>
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Tab Bar - Visible only on mobile */}
      {isMobile && (
        <MobileBottomTabs
          items={navigation.mobile}
          activeId={activeNavId}
          onNavigate={onNavigate}
          allItems={navigation.desktop}
        />
      )}

      {/* Site-wide Footer (existing component) */}
      <Footer />
    </div>
  );
};

const styles = {
  layoutContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-gray-50)',
    fontFamily: 'var(--font-family)',
  },

  mainWrapper: {
    display: 'flex',
    flex: 1,
    maxWidth: 'var(--layout-total-width)',
    margin: '0 auto',
    width: '100%',
  },

  mainContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-6)',
    paddingBottom: 'calc(var(--space-6) + var(--bottom-tab-height))', // Extra padding on mobile for bottom tabs
  },

  contentContainer: {
    width: '100%',
    maxWidth: 'var(--layout-max-width)',
  },
};

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  userRole: PropTypes.oneOf(['admin', 'guide', 'customer']),
  activeNavId: PropTypes.string,
  onNavigate: PropTypes.func,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.string,
  }),
};

export default DashboardLayout;
